import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  X, ArrowRight, ArrowLeft, Check, PiggyBank, Calendar, CalendarDays,
  Utensils, Gift, Plane, Car, Home, ShoppingBag, Heart,
  Sparkles, TrendingUp, Wallet, AlertCircle, Zap, HandCoins, Repeat
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import FrequencySelector from '@/components/ui/FrequencySelector';
import { 
  FREQUENCIES as ALL_FREQUENCIES,
  getFrequencyByValue, 
  getFrequencyLabel,
  calculateCustomMultiplier,
  requiresDayOfMonth
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Fréquences autorisées pour les portefeuilles
// Inclut: unique (ponctuelle), hebdomadaire, quinzaine, mensuel, bimestriel, trimestriel, semestriel, annuel, personnalise
const PORTEFEUILLE_FREQUENCIES = ALL_FREQUENCIES.filter(f => 
  !['quotidien'].includes(f.value) // Exclure quotidien (non pertinent pour les portefeuilles)
);

// Suggestions prédéfinies avec icônes
const SUGGESTIONS = [
  { name: 'Repas du midi', icon: Utensils, color: 'from-orange-400 to-amber-500' },
  { name: 'Cadeau anniversaire', icon: Gift, color: 'from-pink-400 to-rose-500' },
  { name: 'Vacances', icon: Plane, color: 'from-cyan-400 to-blue-500' },
  { name: 'Réparation voiture', icon: Car, color: 'from-gray-400 to-slate-500' },
  { name: 'Loyer / Charges', icon: Home, color: 'from-emerald-400 to-teal-500' },
  { name: 'Shopping', icon: ShoppingBag, color: 'from-purple-400 to-indigo-500' },
  { name: 'Santé', icon: Heart, color: 'from-red-400 to-rose-500' },
  { name: 'Épargne projet', icon: Sparkles, color: 'from-amber-400 to-yellow-500' },
];

// Modes de portefeuille (ponctuelle vs récurrente)
const MODES = [
  { 
    value: 'ponctuelle', 
    label: 'Ponctuelle', 
    desc: 'Un seul versement à mettre de côté',
    icon: Zap,
    color: 'from-violet-500 to-purple-600',
    detail: 'Idéal pour une dépense prévue à date fixe'
  },
  { 
    value: 'recurrente', 
    label: 'Récurrente', 
    desc: 'Versements réguliers automatiques',
    icon: Repeat,
    color: 'from-teal-500 to-cyan-600',
    detail: 'Idéal pour constituer une réserve progressivement'
  },
];

const CreatePortefeuilleWizard = ({ open, onClose, calendarId, currentBalance, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    selectedSuggestion: null,
    type: 'ponctuelle', // 'ponctuelle' ou 'recurrente'
    // Système de fréquence harmonisé
    frequence: 'mensuel',
    frequencyUnit: 'mois',
    frequencyValue: 1,
    montant: '',
    montant_initial: '',
    date_debut: new Date().toISOString().split('T')[0],
    jour_prelevement: new Date().getDate()
  });

  // Reset form when closing
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setShowSuccess(false);
        setFormData({
          nom: '',
          selectedSuggestion: null,
          type: 'ponctuelle',
          frequence: 'mensuel',
          frequencyUnit: 'mois',
          frequencyValue: 1,
          montant: '',
          montant_initial: '',
          date_debut: new Date().toISOString().split('T')[0],
          jour_prelevement: new Date().getDate()
        });
      }, 300);
    }
  }, [open]);

  // Calculs dynamiques - système harmonisé
  const calculations = useMemo(() => {
    const montant = parseFloat(formData.montant) || 0;
    const montantInitial = parseFloat(formData.montant_initial) || 0;
    
    let projectionMensuelle = 0;
    let projection3Mois = 0;
    let projectionAnnuelle = 0;
    let monthlyMultiplier = 0;
    
    if (formData.type === 'ponctuelle') {
      // Ponctuelle : pas de récurrence
      projectionMensuelle = montant;
      projection3Mois = montant;
      projectionAnnuelle = montant;
    } else {
      // Récurrente : utiliser le système harmonisé
      if (formData.frequence === 'personnalise') {
        monthlyMultiplier = calculateCustomMultiplier(formData.frequencyUnit, formData.frequencyValue);
      } else {
        const freq = getFrequencyByValue(formData.frequence);
        monthlyMultiplier = freq?.multiplierToMonthly || 1;
      }
      
      projectionMensuelle = montant * monthlyMultiplier;
      projection3Mois = projectionMensuelle * 3;
      projectionAnnuelle = projectionMensuelle * 12;
    }
    
    const impactSoldeMensuel = formData.type === 'ponctuelle' ? -montant : -projectionMensuelle;
    const nouveauSolde = (currentBalance || 0) + impactSoldeMensuel;
    
    return {
      projectionMensuelle,
      projection3Mois,
      projectionAnnuelle,
      impactSoldeMensuel,
      nouveauSolde,
      montantInitial,
      monthlyMultiplier
    };
  }, [formData.montant, formData.type, formData.frequence, formData.frequencyUnit, formData.frequencyValue, formData.montant_initial, currentBalance]);

  const handleSuggestionClick = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      nom: suggestion.name,
      selectedSuggestion: suggestion
    }));
  };

  // Handler pour le changement de mode (ponctuelle vs récurrente)
  const handleModeSelect = (mode) => {
    setFormData(prev => ({
      ...prev,
      type: mode.value,
      // Si récurrente, mettre mensuel par défaut
      frequence: mode.value === 'recurrente' ? 'mensuel' : prev.frequence
    }));
  };

  // Handler pour le changement de fréquence (système harmonisé)
  const handleFrequencyChange = (frequencyType, frequencyUnit, frequencyValue) => {
    setFormData(prev => ({
      ...prev,
      frequence: frequencyType,
      frequencyUnit: frequencyUnit,
      frequencyValue: frequencyValue
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.nom.trim().length > 0;
      case 2: return true;
      case 3: return parseFloat(formData.montant) > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        nom: formData.nom.trim(),
        type: formData.type,
        compte: 'personal',
        montant_initial: parseFloat(formData.montant_initial) || 0,
        date_debut: formData.date_debut,
        jour_prelevement: formData.jour_prelevement
      };

      // Si récurrente, ajouter les champs de fréquence
      if (formData.type === 'recurrente') {
        payload.montant_recurrent = parseFloat(formData.montant);
        payload.frequence = formData.frequence;
        // Système de fréquence harmonisé
        payload.frequency_unit = formData.frequencyUnit;
        payload.frequency_value = formData.frequencyValue;
      } else {
        // Ponctuelle : montant unique
        payload.montant_recurrent = null;
        payload.frequence = null;
      }

      await axios.post(
        `${API}/portefeuilles/${calendarId}`,
        payload,
        { withCredentials: true }
      );

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error creating portefeuille:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    if (formData.selectedSuggestion) {
      return formData.selectedSuggestion.icon;
    }
    return PiggyBank;
  };

  const getGradient = () => {
    if (formData.selectedSuggestion) {
      return formData.selectedSuggestion.color;
    }
    return 'from-teal-400 to-cyan-500';
  };

  if (!open) return null;

  const IconComponent = getIcon();

  // Animation variants
  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 100 : -100, opacity: 0 })
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-md max-h-[95vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Success Animation */}
          {showSuccess ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${getGradient()} flex items-center justify-center mb-6`}
              >
                <Check className="h-12 w-12 text-white" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                Bonne idée ! 💡
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-gray-600"
              >
                Tu anticipes intelligemment. Ta portefeuille "{formData.nom}" est prête !
              </motion.p>
            </motion.div>
          ) : (
            <>
              {/* Header */}
              <div className={`bg-gradient-to-r ${getGradient()} p-4 sticky top-0 z-10`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      key={formData.selectedSuggestion?.name || 'default'}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring' }}
                      className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"
                    >
                      <IconComponent className="h-5 w-5 text-white" />
                    </motion.div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Nouvelle portefeuille</h2>
                      <p className="text-xs text-white/70">Étape {step} sur 4</p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-white"
                    initial={{ width: 0 }}
                    animate={{ width: `${(step / 4) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Steps Content */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
                <AnimatePresence mode="wait" custom={step}>
                  {/* Step 1: Name */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">À quoi sert ta portefeuille ?</h3>
                        <p className="text-sm text-gray-500">
                          Une portefeuille te permet d'anticiper une dépense sans déséquilibrer ton budget.
                        </p>
                      </div>

                      {/* Suggestions */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {SUGGESTIONS.map((suggestion) => (
                          <motion.button
                            key={suggestion.name}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                              formData.nom === suggestion.name 
                                ? 'border-teal-500 bg-teal-50' 
                                : 'border-gray-200 hover:border-teal-300'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${suggestion.color} flex items-center justify-center`}>
                              <suggestion.icon className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate">{suggestion.name}</span>
                          </motion.button>
                        ))}
                      </div>

                      {/* Custom name input */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Ou saisis un nom personnalisé
                        </label>
                        <Input
                          value={formData.nom}
                          onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value, selectedSuggestion: null }))}
                          placeholder="Ex: Projet maison, Anniversaire maman..."
                          className="text-base"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Mode (ponctuelle vs récurrente) + Fréquence */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Comment veux-tu alimenter ta portefeuille ?</h3>
                        <p className="text-sm text-gray-500">
                          Choisis le mode qui correspond à ton besoin
                        </p>
                      </div>

                      {/* Choix du mode */}
                      <div className="space-y-3">
                        {MODES.map((mode) => (
                          <motion.button
                            key={mode.value}
                            type="button"
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() => handleModeSelect(mode)}
                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                              formData.type === mode.value 
                                ? 'border-teal-500 bg-teal-50' 
                                : 'border-gray-200 hover:border-teal-300'
                            }`}
                            data-testid={`mode-btn-${mode.value}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mode.color} flex items-center justify-center`}>
                                <mode.icon className="h-6 w-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{mode.label}</h4>
                                <p className="text-sm text-gray-500">{mode.desc}</p>
                              </div>
                              {formData.type === mode.value && (
                                <Check className="h-5 w-5 text-teal-600" />
                              )}
                            </div>
                          </motion.button>
                        ))}
                      </div>

                      {/* Si mode récurrent, afficher le sélecteur de fréquence harmonisé */}
                      {formData.type === 'recurrente' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="pt-4"
                        >
                          <FrequencySelector
                            value={formData.frequence}
                            onChange={handleFrequencyChange}
                            frequencyUnit={formData.frequencyUnit}
                            frequencyValue={formData.frequencyValue}
                            variant="full"
                            colorScheme="teal"
                            allowedFrequencies={PORTEFEUILLE_FREQUENCIES.filter(f => f.value !== 'unique').map(f => f.value)}
                            showDescription={true}
                          />
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                  {/* Step 3: Amount */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          Quel montant ?
                        </h3>
                        <p className="text-sm text-gray-500">
                          {formData.type === 'ponctuelle' 
                            ? 'Montant unique à mettre de côté'
                            : `Montant à verser (${getFrequencyLabel(formData.frequence, formData.frequencyUnit, formData.frequencyValue)})`
                          }
                        </p>
                      </div>

                      {/* Amount input with visual feedback */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6">
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.montant}
                            onChange={(e) => setFormData(prev => ({ ...prev, montant: e.target.value }))}
                            placeholder="0"
                            className="text-4xl font-bold text-center h-16 bg-transparent border-none shadow-none focus:ring-0 text-gray-900"
                            data-testid="montant-input"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">€</span>
                        </div>
                        
                        {/* Quick amount buttons */}
                        <div className="flex gap-2 mt-4 justify-center">
                          {[20, 50, 100, 200].map(amount => (
                            <button
                              key={amount}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, montant: amount.toString() }))}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                formData.montant === amount.toString()
                                  ? 'bg-teal-500 text-white'
                                  : 'bg-white text-gray-600 hover:bg-teal-50'
                              }`}
                            >
                              {amount}€
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Dynamic projection - Only for recurring */}
                      {parseFloat(formData.montant) > 0 && formData.type === 'recurrente' && (
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-4"
                        >
                          <div className="flex items-start gap-3">
                            <Sparkles className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2">
                              <p className="text-sm text-teal-800 font-medium">
                                Tu mettras de côté :
                              </p>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-white/70 rounded-lg p-2">
                                  <div className="text-lg font-bold text-teal-700">{calculations.projectionMensuelle.toFixed(0)}€</div>
                                  <div className="text-xs text-gray-500">/ mois</div>
                                </div>
                                <div className="bg-white/70 rounded-lg p-2">
                                  <div className="text-lg font-bold text-teal-700">{calculations.projection3Mois.toFixed(0)}€</div>
                                  <div className="text-xs text-gray-500">en 3 mois</div>
                                </div>
                                <div className="bg-white/70 rounded-lg p-2">
                                  <div className="text-lg font-bold text-teal-700">{calculations.projectionAnnuelle.toFixed(0)}€</div>
                                  <div className="text-xs text-gray-500">en 1 an</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Date de début - for recurring portfolios only */}
                      {formData.type === 'recurrente' && (
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4"
                        >
                          <div className="flex items-start gap-3">
                            <CalendarDays className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 space-y-3">
                              <div>
                                <p className="text-sm text-purple-800 font-medium mb-1">
                                  Quand commencer ?
                                </p>
                                <p className="text-xs text-purple-600">
                                  Premier versement à cette date
                                </p>
                              </div>
                              <Input
                                type="date"
                                value={formData.date_debut}
                                onChange={(e) => setFormData(prev => ({ ...prev, date_debut: e.target.value }))}
                                min={new Date().toISOString().split('T')[0]}
                                className="bg-white border-purple-200 focus:border-purple-400"
                                data-testid="date-debut-input"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Initial deposit removed - users must use manual deposit */}
                      <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-4 border border-blue-200">
                        <p className="text-sm text-blue-800">
                          💡 <strong>Conseil :</strong> Une fois ta portefeuille créée, tu pourras y ajouter des fonds via le bouton "Dépôt" si nécessaire.
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: Summary & Impact */}
                  {step === 4 && (
                    <motion.div
                      key="step4"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Récapitulatif</h3>
                        <p className="text-sm text-gray-500">
                          Vérifie les détails avant de créer ta portefeuille
                        </p>
                      </div>

                      {/* Summary card */}
                      <div className={`bg-gradient-to-br ${getGradient()} rounded-2xl p-4 text-white`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <IconComponent className="h-6 w-6" />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg">{formData.nom}</h4>
                            <p className="text-sm text-white/80">
                              {formData.type === 'ponctuelle' 
                                ? 'Portefeuille ponctuelle' 
                                : getFrequencyLabel(formData.frequence, formData.frequencyUnit, formData.frequencyValue)
                              }
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white/10 rounded-xl p-3 text-center">
                            <div className="text-2xl font-bold">{parseFloat(formData.montant || 0).toFixed(0)}€</div>
                            <div className="text-xs text-white/70">
                              {formData.type === 'ponctuelle' ? 'Montant total' : 'Par versement'}
                            </div>
                          </div>
                          {formData.type === 'recurrente' && (
                            <div className="bg-white/10 rounded-xl p-3 text-center">
                              <div className="text-2xl font-bold">{calculations.projectionMensuelle.toFixed(0)}€</div>
                              <div className="text-xs text-white/70">Par mois</div>
                            </div>
                          )}
                        </div>

                        {/* Date de début */}
                        {formData.type === 'recurrente' && (
                          <div className="mt-3 pt-3 border-t border-white/20">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-white/80 flex items-center gap-2">
                                <CalendarDays className="h-4 w-4" />
                                Premier versement
                              </span>
                              <span className="font-medium">
                                {new Date(formData.date_debut).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric' 
                                })}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Impact on budget */}
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-gray-600" />
                          Impact sur ton budget
                        </h4>
                        
                        <div className="space-y-2 text-sm">
                          {currentBalance !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Solde actuel</span>
                              <span className="font-medium">{currentBalance?.toFixed(0) || 0}€</span>
                            </div>
                          )}
                          {formData.type === 'recurrente' && (
                            <div className="flex justify-between text-amber-600">
                              <span>Impact mensuel</span>
                              <span className="font-medium">{calculations.impactSoldeMensuel.toFixed(0)}€</span>
                            </div>
                          )}
                        </div>

                        {calculations.impactSoldeMensuel < -100 && formData.type === 'recurrente' && (
                          <div className="flex items-start gap-2 bg-amber-50 rounded-lg p-3 mt-2">
                            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700">
                              Cette portefeuille représente un effort conséquent. Assure-toi que ça reste soutenable ! 💪
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Calendar integration note */}
                      <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
                        <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="text-blue-800 font-medium">Intégration au calendrier</p>
                          <p className="text-blue-600 text-xs mt-1">
                            {formData.type === 'ponctuelle' 
                              ? "L'opération sera visible dans ton calendrier à la date du dépôt."
                              : `Les versements apparaîtront automatiquement dans ton calendrier (${getFrequencyLabel(formData.frequence, formData.frequencyUnit, formData.frequencyValue)}).`
                            }
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer with navigation */}
              <div className="p-4 border-t bg-gray-50">
                <div className="flex gap-3">
                  {step > 1 && (
                    <Button 
                      variant="outline" 
                      onClick={handleBack}
                      className="flex-1"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Retour
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className={`flex-1 bg-gradient-to-r ${getGradient()} hover:opacity-90`}
                  >
                    {loading ? (
                      'Création...'
                    ) : step === 4 ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Créer ma portefeuille
                      </>
                    ) : (
                      <>
                        Continuer
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreatePortefeuilleWizard;
